/**
 * Simple WebSocket wrapper for sending fast key commands to the remote control WS.
 *
 * Usage:
 *   import FastKeySocket from '../utils/fastKeySocket';
 *   const sock = new FastKeySocket();
 *   sock.connect({ url: 'wss://example', autoReconnect: true });
 *   sock.sendKey(mac, 20);
 *
 * Notes:
 * - Browsers do not allow setting arbitrary headers on WebSocket handshakes. If the
 *   fast UI requires a cookie for auth, that cookie must be set on the browser for
 *   the WS host's origin before calling connect (document.cookie for the domain),
 *   or the server must accept a token via query string.
 */

type ConnectOptions = {
    url?: string; // wss://... or ws://...; if not provided, caller must set later
    token?: string; // optional token to append as query param if server supports it
    autoReconnect?: boolean;
    reconnectIntervalMs?: number;
};

export default class FastKeySocket {
    private ws: WebSocket | null = null;
    private url?: string;
    private opts: ConnectOptions = {} as ConnectOptions;
    private manuallyClosed = false;

    // Event handlers the consumer can set
    public onopen: (() => void) | null = null;
    public onclose: ((ev: CloseEvent) => void) | null = null;
    public onerror: ((ev: Event) => void) | null = null;
    public onmessage: ((data: any) => void) | null = null;

    connect(opts: ConnectOptions) {
        this.opts = { reconnectIntervalMs: 2000, autoReconnect: true, ...opts };
        if (!opts.url) {
            console.warn('FastKeySocket.connect called without url; waiting for connect() with url');
            return;
        }
        this.url = opts.url;
        this.manuallyClosed = false;
        this.open();
    }

    private open() {
        if (!this.url) return;

        // If token provided, append as query param
        let url = this.url;
        if (this.opts.token) {
            const sep = url.includes('?') ? '&' : '?';
            url = `${url}${sep}token=${encodeURIComponent(this.opts.token)}`;
        }

        try {
            this.ws = new WebSocket(url);
        } catch (e) {
            console.error('Failed to construct WebSocket', e);
            this.scheduleReconnect();
            return;
        }

        this.ws.onopen = () => {
            if (this.onopen) this.onopen();
        };
        this.ws.onclose = (ev) => {
            this.ws = null;
            if (this.onclose) this.onclose(ev);
            if (!this.manuallyClosed && this.opts.autoReconnect) this.scheduleReconnect();
        };
        this.ws.onerror = (ev) => {
            if (this.onerror) this.onerror(ev);
        };
        this.ws.onmessage = (ev) => {
            let parsed: any = ev.data;
            try { parsed = JSON.parse(ev.data); } catch (e) { /* keep raw */ }
            if (this.onmessage) this.onmessage(parsed);
        };
    }

    private scheduleReconnect() {
        if (this.manuallyClosed || !this.opts.autoReconnect) return;
        const t = this.opts.reconnectIntervalMs ?? 2000;
        setTimeout(() => {
            if (!this.manuallyClosed) this.open();
        }, t);
    }

    send(obj: any) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket not open; cannot send');
            return false;
        }
        try {
            const txt = typeof obj === 'string' ? obj : JSON.stringify(obj);
            this.ws.send(txt);
            return true;
        } catch (e) {
            console.error('Failed to send over ws', e);
            return false;
        }
    }

    sendKey(mac: string, keyParam: number) {
        // The fast UI observed frame: { mac: '...', action: 'cmd_key_code', param: 20 }
        const frame = { mac, action: 'cmd_key_code', param: keyParam };
        return this.send(frame);
    }

    close() {
        this.manuallyClosed = true;
        if (this.ws) {
            try { this.ws.close(); } catch (e) { /* ignore */ }
            this.ws = null;
        }
    }
}
